import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Grid, 
  Stack
} from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { StepProps } from '../types';
import FileUploadField from './FileUploadField';

const CancellationCheck: React.FC<StepProps> = ({ onCancellationResponse, onNext }) => {
  const { formData, updateFormData } = useFormContext();
  const [showCancellationReason, setShowCancellationReason] = useState<boolean>(false);
  
  // Initialize the component state based on formData
  useEffect(() => {
    if (formData?.isCancelled) {
      setShowCancellationReason(true);
    }
  }, [formData]);
  
  const handleCancellation = (isCancelled: boolean) => {
    // Update the formData
    updateFormData('isCancelled', isCancelled);
    
    // Update local state to control display
    setShowCancellationReason(isCancelled);
    
    // If cancelled, we can also capture a reason
    if (isCancelled) {
      updateFormData('cancellationReason', '');
      // Clear any existing invoice files to prevent issues
      updateFormData('invoiceFiles', []);
    }
    
    // Only notify parent about cancellation status
    if (onCancellationResponse) {
      onCancellationResponse(isCancelled);
    }
  };

  // Format the date if it exists
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    
    // Parse the date without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date with local timezone (months are 0-indexed in JavaScript)
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',  
      day: 'numeric'
    });
  };

  // Handle form submission for cancelled jobs
  const handleSubmitCancellation = () => {
    // Call the onNext function from props to move to the next step
    if (onNext) {
      onNext();
    }
  };
  
  // Handle returning to the cancellation question
  const handleGoBack = () => {
    setShowCancellationReason(false);
    updateFormData('isCancelled', false);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h2" component="h2" gutterBottom>
          Job Status Confirmation
        </Typography>
        
        <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" component="div">
                Job Number:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formData?.jobNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" component="div">
                Date:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(formData?.jobDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" component="div">
                Case Name:
              </Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                {formData?.caseName}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        
        {!showCancellationReason ? (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 3, 
              mb: 3, 
              backgroundColor: '#f8f9fa',
              borderLeft: '3px solid #ED7420'
            }}
          >
            <Typography variant="h3" component="h3" gutterBottom>
              Was this job cancelled?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If the job was cancelled, you&apos;ll only need to confirm the cancellation to complete this form.
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              sx={{ mt: 3 }}
            >              
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleCancellation(true)}
              >
                Yes, this job was cancelled
              </Button>

              <Button 
                variant="contained"
                color="primary"
                onClick={() => handleCancellation(false)}
              >
                No, this job took place
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Box sx={{ mt: 3 }}>
            <TextField 
              id="cancellation-reason"
              label="Cancellation Reason and notes (other than flat fee)"
              placeholder="Please provide any details about the cancellation, including payroll information and any additional fees other than your flat fee"
              value={formData?.cancellationReason || ''}
              onChange={(e) => updateFormData('cancellationReason', e.target.value)}
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              sx={{ mb: 3 }}
            />
            
            <FileUploadField
              title="Upload Invoice (required for any fees)"
              type="invoiceFile"
              required={false}
              error={undefined}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button 
                variant="outlined"
                color="primary"
                onClick={handleGoBack}
              >
                Go Back
              </Button>
              
              <Button 
                variant="contained"
                color="primary"
                onClick={handleSubmitCancellation}
              >
                Submit Cancellation
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CancellationCheck;